// Industry-specific content used across all doc pages.
// Add a new entry here to support a new industry; pages consume this via useIndustry().

export const INDUSTRIES = [
  {
    id: 'logistics',
    label: 'Logistics',

    product: {
      companyName: 'Acme Logistics',
      products: ['Logistics-as-a-Service', 'Warehousing', 'Last-Mile Delivery'],
      primaryProduct: 'Logistics-as-a-Service',
      singleTagExample: {
        tagDim: 'Zone',
        tagValues: ['Zone-A', 'Zone-B', 'Zone-C'],
        feeComponents: [
          { name: 'Courier Charges',  unit: 'delivery', rates: ['₹40', '₹55', '₹70'] },
          { name: 'Tracking Charges', unit: 'shipment', rates: ['₹5',  '₹5',  '₹8']  },
          { name: 'Fuel Surcharge',   unit: 'km',       rates: ['₹2',  '₹2.50','₹3'] },
        ],
      },
      multiTagExample: {
        tags: [
          { label: 'Brand', values: ['Brand-1', 'Brand-2'] },
          { label: 'Zone',  values: ['Zone-A',  'Zone-B']  },
        ],
      },
      tree: [
        { name: 'Logistics-as-a-Service', tags: ['Zone', 'Vehicle Type'],      components: ['Courier Charges', 'Tracking Charges', 'Fuel Surcharge'] },
        { name: 'Warehousing',            tags: ['Warehouse Location'],         components: ['Storage Fee', 'Handling Fee', 'Palletization Fee']      },
        { name: 'Last-Mile Delivery',     tags: ['Zone', 'Brand'],              components: ['Delivery Fee', 'Failed Attempt Fee', 'COD Charges']     },
      ],
      usageEvent: {
        product: 'Logistics-as-a-Service',
        tags: 'Zone = Zone-A, Brand = Brand-1',
        feeComponent: 'Courier Charges',
        quantity: '42 deliveries',
      },
    },

    feeComponent: {
      examples: ['Courier Charges', 'Tracking Charges', 'Fuel Surcharge'],
      invoice: {
        id: 'INV-2024-0041',
        due: 'Due Jan 31, 2024',
        rows: [
          { accent: 'purple', name: 'Courier Charges',  note: '50 deliveries',     qty: '50',  rate: '₹40.00', amount: '₹2,000.00' },
          { accent: 'teal',   name: 'Tracking Charges', note: '50 shipments',       qty: '50',  rate: '₹5.00',  amount: '₹250.00'   },
          { accent: 'amber',  name: 'Fuel Surcharge',   note: '620 km distance',    qty: '620', rate: '₹2.00',  amount: '₹1,240.00' },
        ],
        total: '₹3,490.00',
      },
    },

    rateAttributes: {
      useCases: [
        ['Zone-based delivery pricing',  'Zone',           'Zone-A, Zone-B, Zone-C'],
        ['Multi-client 3PL platform',    'Brand',          'Brand-A, Brand-B'],
        ['Brand + zone pricing',         'Brand, Zone',    'Brand-A × Zone-A …'],
        ['Vehicle type surcharge',       'Vehicle',        '2-Wheeler, 4-Wheeler'],
      ],
      singleDim: {
        intro: 'A 3PL pricing courier charges differently by delivery zone defines a Zone dimension on the product:',
        headers: ['Zone'],
        rows: [['Zone-A'], ['Zone-B'], ['Zone-C']],
        outro: 'Fee components that use Zone are priced once per zone. Each carries its own per-zone rate in the pricing plan.',
        dimLabel: 'Zone',
      },
      multiDim: {
        intro: 'A platform serving multiple brands across multiple zones defines Brand and Zone on the product:',
        headers: ['Brand', 'Zone'],
        rows: [
          ['Brand-1', 'Zone-A'],
          ['Brand-1', 'Zone-B'],
          ['Brand-2', 'Zone-A'],
          ['Brand-2', 'Zone-B'],
        ],
        dims: [
          { key: 'brand', label: 'Brand', values: ['Brand-1', 'Brand-2'] },
          { key: 'zone',  label: 'Zone',  values: ['Zone-A',  'Zone-B']  },
        ],
      },
      // Per-component tag selection: product defines all dims, each component uses a subset
      partialTags: {
        productDims: [
          { key: 'zone',    label: 'Zone',         values: ['Zone-A', 'Zone-B'] },
          { key: 'brand',   label: 'Brand',         values: ['Brand-1', 'Brand-2'] },
          { key: 'vehicle', label: 'Vehicle Type',  values: ['2-Wheeler', '4-Wheeler'] },
        ],
        components: [
          { name: 'Courier Charges',  uses: ['zone', 'brand'] },
          { name: 'Fuel Surcharge',   uses: ['zone', 'vehicle'] },
          { name: 'Tracking Charges', uses: ['zone'] },
        ],
      },
      invoiceBehavior: 'A logistics customer with deliveries across two zones sees two Courier Charges lines on their invoice — one per zone — each with its own quantity and rate.',
    },

    billableMetric: {
      product: 'Logistics-as-a-Service',
      currentFlow: {
        feeComponent: 'Demurrage Charges',
        usageAttribute: 'Warehouse Days',
        unit: 'warehouse days',
        groups: [
          { refId: 'BAG_1', calc: '3 + 6', total: 9  },
          { refId: 'BAG_2', calc: '9',     total: 9  },
          { refId: 'BAG_3', calc: '12',    total: 12 },
        ],
        finalSum: 30,
        rateCardLabel: '₹2 per warehouse day (flat)',
        rateCardCalc: '30 × ₹2 = ₹60',
        invoiceLine: 'Demurrage Charges',
        charge: '₹60',
        transitionNote: 'Notice what happened at step 2 — BAG_1\'s two rows (3 FWD + 6 RTO) collapsed into a single 9-day value. This works if you are happy to charge demurrage on both journeys. But if you only want to charge RTO bags, there is no way to express that in this flow.',
      },
      problems: [
        { title: 'Charging a subset of events',          desc: 'The sum includes all events regardless of their product attributes. If demurrage applies only to RTO bags, there is no way to exclude FWD rows from the sum.' },
        { title: 'Recorded unit ≠ rate card unit',        desc: 'If the recorded unit is a block of 5 real days but the rate card is in real days, the values going into the rate card are wrong by a factor of 5.' },
        { title: 'Different formula per event type',      desc: 'If FWD bags should be priced on Loaded Wt only and RTO bags on Loaded Wt + Unloaded Wt, a single sum cannot express that split.' },
        { title: 'Threshold charging with accurate counts', desc: 'A zero-rate tier workaround produces the right total, but the invoice count includes events that were never charged — which confuses customers.' },
      ],
      filterExamples:     ['Journey Type = RTO', 'Warehouse Days > 3', 'Vehicle Type = HCV'],
      expressionExamples: ['Warehouse Days × 5', 'Loaded Wt + Unloaded Wt', 'Loaded Wt'],
      eventCols: [
        { key: 'refId',       label: 'Usage Ref ID',      type: 'ref'     },
        { key: 'loadedWt',    label: 'Loaded Wt',         type: 'usage'   },
        { key: 'unloadedWt',  label: 'Unloaded Wt',       type: 'usage'   },
        { key: 'whDays',      label: 'Warehouse Days',    type: 'usage'   },
        { key: 'journeyType', label: 'Journey Type',      type: 'product' },
      ],
      events: [
        { refId: 'BAG_1', loadedWt: 2,  unloadedWt: 1,  whDays: 3,  journeyType: 'FWD' },
        { refId: 'BAG_1', loadedWt: 4,  unloadedWt: 3,  whDays: 6,  journeyType: 'RTO' },
        { refId: 'BAG_2', loadedWt: 6,  unloadedWt: 5,  whDays: 9,  journeyType: 'FWD' },
        { refId: 'BAG_3', loadedWt: 8,  unloadedWt: 7,  whDays: 12, journeyType: 'FWD' },
      ],
      filterScenario: {
        fee: 'Demurrage Charges',
        problem: 'BAG_1 has two rows — one FWD, one RTO. The grouping step sums warehouse days for both: 3 + 6 = 9. But demurrage should only apply to the RTO row (6 days). There is no way to express "only RTO" without a filter.',
        metricName: 'RTO Warehouse Days',
        filter: 'Journey Type = RTO',
        expression: 'Warehouse Days',
        aggregation: 'Sum',
        before: 'BAG_1: 9 days  ·  BAG_2: 9 days  ·  BAG_3: 12 days  (FWD days incorrectly included)',
        after:  'BAG_1: 6 days  (only the RTO row passes through)',
      },
      transformScenario: {
        fee: 'Demurrage Charges',
        problem: 'Each recorded warehouse "day" is a block of 5 real days. The rate card is expressed in real days (0–10: ₹2/day, 10+: ₹1/day). Without a transform the billing engine receives block counts, putting values in the wrong tier.',
        metricName: 'Demurrage Real Days',
        filter: '(none)',
        expression: 'Warehouse Days × 5',
        aggregation: 'Sum',
        before: '30 blocks piped to rate card — tier boundaries evaluate incorrectly',
        after:  '150 real days piped to rate card — tier boundaries evaluate correctly',
      },
      conditionalScenario: {
        fee: 'Shipment Fees',
        problem: 'FWD bags should be charged on Loaded Wt only. RTO bags should be charged on Loaded Wt + Unloaded Wt. A single meter cannot express a formula that changes per journey type.',
        metrics: [
          { name: 'FWD Shipment Weight', filter: 'Journey Type = FWD', expression: 'Loaded Wt',               aggregation: 'Sum', result: '2 + 6 + 8 = 16 kg' },
          { name: 'RTO Shipment Weight', filter: 'Journey Type = RTO', expression: 'Loaded Wt + Unloaded Wt', aggregation: 'Sum', result: '(4 + 3) = 7 kg' },
        ],
      },
      thresholdScenario: {
        fee: 'Demurrage Charges',
        problem: 'Demurrage applies only to bags with more than 3 warehouse days. A rate card workaround (0–3 days: ₹0) produces the correct total but reports a bag count of 3 — customers see all three bags on the invoice, not the expected two.',
        metricName: 'Demurrage-Eligible Days',
        filter: 'Warehouse Days > 3',
        expression: 'Warehouse Days',
        aggregation: 'Sum',
        before: '3 bags counted — includes BAG_1 FWD (3 days) at ₹0, misleading customers',
        after:  '2 bags counted: BAG_2 (9 days) and BAG_3 (12 days) — count and value both accurate',
      },
      metrics: [
        { name: 'RTO Warehouse Days',   fee: 'Demurrage Charges', filter: 'Journey Type = RTO', expression: 'Warehouse Days',              aggregation: 'Sum' },
        { name: 'FWD Shipment Weight',  fee: 'Shipment Fees',     filter: 'Journey Type = FWD', expression: 'Loaded Wt',                   aggregation: 'Sum' },
        { name: 'RTO Shipment Weight',  fee: 'Shipment Fees',     filter: 'Journey Type = RTO', expression: 'Loaded Wt + Unloaded Wt',     aggregation: 'Sum' },
      ],
    },

    plan: {
      currency: '₹',
      plans: [
        {
          name: 'Silver Plan',
          rateCard: 5000,
          duration: '1 month',
          fees: [
            {
              product: 'Logistics-as-a-Service',
              name: 'Courier Charges',
              paymentMode: 'prepaid',
              startsAt: 'With contract',
              duration: '1 month',
              billingCycle: '1 month',
              grant: { type: 'Quantity-only', amount: 500, unit: 'shipments' },
              overageRate: '₹40 / shipment', overageRateNum: 40,
            },
            {
              product: 'Logistics-as-a-Service',
              name: 'Fuel Surcharge',
              paymentMode: 'prepaid',
              startsAt: 'With contract',
              duration: '1 month',
              billingCycle: '1 month',
              grant: { type: 'Quantity-only', amount: 2000, unit: 'km' },
              overageRate: '₹2 / km', overageRateNum: 2,
            },
          ],
        },
        {
          name: 'Gold Plan',
          rateCard: 12000,
          duration: '1 month',
          fees: [
            {
              product: 'Logistics-as-a-Service',
              name: 'Courier Charges',
              paymentMode: 'prepaid',
              startsAt: 'With contract',
              duration: '1 month',
              billingCycle: '1 month',
              grant: { type: 'Quantity-only', amount: 1500, unit: 'shipments' },
              overageRate: '₹35 / shipment', overageRateNum: 35,
            },
            {
              product: 'Logistics-as-a-Service',
              name: 'Fuel Surcharge',
              paymentMode: 'prepaid',
              startsAt: 'With contract',
              duration: '1 month',
              billingCycle: '1 month',
              grant: { type: 'Quantity-only', amount: 6000, unit: 'km' },
              overageRate: '₹1.50 / km', overageRateNum: 1.5,
            },
          ],
        },
      ],
      overageExample: {
        plan: 'Silver Plan',
        planPrice: 5000,
        fee: 'Courier Charges',
        included: 500,
        used: 620,
        overage: 120,
        overageRate: 40,
        overageCharge: 4800,
        unit: 'shipments',
        otherFee: { name: 'Fuel Surcharge', used: 1800, included: 2000, overageCharge: 0 },
      },
      contractExample: {
        customer: 'Flipkart Supply Chain Pvt. Ltd.',
        plans: [
          { name: 'Silver Plan', price: '₹5,000' },
        ],
        overages: [
          { fee: 'Courier Charges (overage)', note: '120 excess shipments × ₹40', charge: '₹4,800' },
        ],
        adHocFees: [
          { name: 'COD Charges', note: '240 COD shipments × ₹5', charge: '₹1,200' },
        ],
        total: '₹11,000',
      },
      carryForward: {
        fee: 'Courier Charges',
        included: 500,
        unit: 'shipments',
        cycles: [
          { label: 'Month 1', used: 380, carried: 120, balance: 620 },
          { label: 'Month 2', used: 550, carried: 70,  balance: 570 },
          { label: 'Month 3', used: 600, carried: 0,   balance: 500, overageNote: '30 at ₹40 = ₹1,200' },
        ],
      },
      carryForwardLimited: {
        cfUntilPeriod: 3,
        fees: [
          { name: 'Courier Charges', included: 500,  unit: 'shipments', overageRateNum: 40,  overageRate: '₹40 / shipment' },
          { name: 'Fuel Surcharge',  included: 2000, unit: 'km',        overageRateNum: 2,   overageRate: '₹2 / km'        },
        ],
        // quiet months 1-2 build buffer, buffer is lost at month-3 cutoff, month-4 spike hits overage
        defaultUsages: {
          'Courier Charges': [400, 450, 520, 580, 480],
          'Fuel Surcharge':  [1800, 1700, 2100, 2400, 1900],
        },
      },
    },

    grant: {
      product: 'Logistics-as-a-Service',
      name: 'Starter Courier Allowance',
      description: 'Zero-rates courier weight on Shipping Fee up to 500 kg total, capped at 200 kg per billing cycle.',
      type: 'quantity',
      feeComponent: 'Shipping Fee',
      quantity: { driver: 'Courier Weight', amount: 500, unit: 'kg' },
      perCycleLimit: 200,
      expiryDate: '2026-06-30',
      scopeTable: [
        { fee: 'Shipping Fee',   driver: 'Courier Weight', granted: true,  note: 'Grant applies — zero-rates up to 200 kg/cycle'  },
        { fee: 'Fuel Surcharge', driver: 'Courier Weight', granted: false, note: 'Not in grant scope — billed at full rate'        },
        { fee: 'Demurrage Fee',  driver: 'Warehouse Days', granted: false, note: 'Different meter'                      },
      ],
      billLines: [
        { name: 'Starter Courier Allowance', isGrant: true,  qty: '200 kg', rate: '₹0',  amount: '₹0'     },
        { name: 'Shipping Fee',              isGrant: false, qty: '40 kg',  rate: '₹40', amount: '₹1,600' },
      ],
      cycles: [
        { label: 'Cycle 1', usage: 240, zeroRated: 200, billed: 40,  cumulative: 200, remaining: 300 },
        { label: 'Cycle 2', usage: 180, zeroRated: 180, billed: 0,   cumulative: 380, remaining: 120 },
        { label: 'Cycle 3', usage: 200, zeroRated: 120, billed: 80,  cumulative: 500, remaining: 0   },
      ],
    },

    productAttribute: {
      product: 'Logistics-as-a-Service',
      description: 'Extra context recorded with each shipment event — not used in pricing, but available for analytics and reporting.',
      attributes: [
        { name: 'Journey Type',  type: 'Enum',   values: 'FWD, RTO',                   description: 'Whether the shipment is a forward delivery or a return to origin' },
        { name: 'Service Type',  type: 'Enum',   values: 'Express, Standard, Economy',  description: 'Speed tier selected for the delivery' },
        { name: 'Vehicle Type',  type: 'Enum',   values: '2W, 3W, 4W, HCV',            description: 'Vehicle category used for the delivery' },
      ],
      sampleTable: {
        usageAttrs: ['Loaded Wt (kg)', 'Unloaded Wt (kg)', 'Warehouse Days'],
        productAttrs: ['Journey Type'],
        rows: [
          { refId: 'BAG_1', usageVals: ['2', '1', '3'],  productVals: ['FWD'] },
          { refId: 'BAG_1', usageVals: ['4', '3', '6'],  productVals: ['RTO'] },
          { refId: 'BAG_2', usageVals: ['6', '5', '9'],  productVals: ['FWD'] },
          { refId: 'BAG_3', usageVals: ['8', '7', '12'], productVals: ['FWD'] },
        ],
      },
    },

    usageAttribute: {
      examples: [
        {
          feeComponent: 'Courier Fees',
          attribute: 'Parcel Weight',
          unit: 'kg',
          description: 'The system records the weight of each parcel shipped. That measurement drives the Courier Fees charge. The customer sees the fee — not the raw weight.',
        },
        {
          feeComponent: 'Fuel Surcharge',
          attribute: 'Distance Travelled',
          unit: 'km',
          description: 'The system records kilometres driven per delivery. That distance drives the Fuel Surcharge. Switching to time-based pricing later means adding a new attribute and remapping — the invoice label stays the same.',
        },
      ],
      oneToMany: [
        { attribute: 'Parcel Weight',     feeComponents: ['Courier Fees', 'Tracking Fees'] },
        { attribute: 'Distance Travelled', feeComponents: ['Fuel Surcharge', 'Zone Surcharge'] },
        { attribute: 'Shipment Count',    feeComponents: ['Handling Fee', 'Insurance Fee'] },
      ],
      manyToOne: {
        feeComponent: 'Delivery Charges',
        attributes: ['Parcel Weight', 'Distance Travelled'],
      },
      multipliers: [
        { attribute: 'Parcel Weight (kg)', multiplier: '100',   feeComponent: 'Logistics Fee (units)', effect: '1 kg → 100 fee units' },
        { attribute: 'Distance (km)',      multiplier: '0.5',   feeComponent: 'Fuel Surcharge (₹)',    effect: '1 km → ₹0.50' },
        { attribute: 'Shipment Count',     multiplier: '5',     feeComponent: 'Tracking Fee (₹)',      effect: '1 shipment → ₹5.00' },
      ],
    },

    usageGrouping: {
      feeComponent: 'Courier Charges',
      tiersNote: 'Tier 1: 1–50 deliveries → ₹40 each   ·   Tier 2: 51+ deliveries → ₹30 each',
      records: [
        { qty: '30 deliveries' },
        { qty: '40 deliveries' },
      ],
      separate: {
        refIds: ['morning-batch', 'afternoon-batch'],
        results: [
          { calc: '30 × ₹40  (Tier 1)', charge: '₹1,200' },
          { calc: '40 × ₹40  (Tier 1)', charge: '₹1,600' },
        ],
        total: '₹2,800',
      },
      grouped: {
        refId: 'jan-14-batch',
        aggregatedQty: '70 deliveries',
        tiers: [
          { calc: '50 × ₹40  (Tier 1)', charge: '₹2,000' },
          { calc: '20 × ₹30  (Tier 2)', charge: '₹600' },
        ],
        total: '₹2,600',
      },
    },

    usage: {
      product: 'Logistics-as-a-Service',
      customer: 'Flipkart Supply Chain Pvt. Ltd.',
      dedup: {
        batchId: 'batch-jan-14',
        attr: 'Courier Weight',
        first:  { value: '80 kg',  note: 'sent at 10:42 AM — incorrect weight recorded' },
        second: { value: '95 kg',  note: 'sent at 11:05 AM — corrected after re-weigh' },
      },
      usageAttr: { label: 'Loaded Weight', value: '80', unit: 'kg' },
      rateAttrs: [{ label: 'Zone', value: 'North' }],
      feeComponent: 'Courier Charges',
      timestamp: '14 Jan 2025, 10:42 AM',
      billingCycle: { start: '1 Jan 2025', end: '31 Jan 2025' },
      cycleNote: 'This usage falls inside the January billing cycle and will appear on the January invoice for Flipkart.',
      partialIngestion: {
        rateAttr: { label: 'Zone', value: 'North' },
        contracts: [
          { label: 'Contract A', feeComponent: 'Courier Charges', usageAttr: 'Courier Weight' },
          { label: 'Contract B', feeComponent: 'Distance Fee',    usageAttr: 'Distance Travelled' },
        ],
        measurements: [
          { attr: 'Courier Weight',     value: '80 kg',  matched: true,  reason: 'Rate card for Zone = North found in Contract A' },
          { attr: 'Distance Travelled', value: '50 km',  matched: false, reason: 'No rate card for Zone = North in Contract B' },
        ],
      },
    },

    proration: {
      fixedFee: 500,
      compositeFixed: 100,
      compositeRate: 10,
      compositeUnit: 'delivery',
      compositeUnitPlural: 'deliveries',
    },

    billingReport: {
      report: {
        reportId: 'BR-2025-0041',
        customer: 'Flipkart Supply Chain Pvt. Ltd.',
        period: { start: '1 Jan 2025', end: '31 Jan 2025' },
        rows: [
          { name: 'Courier Charges',  note: '620 deliveries × ₹40',  qty: '620',  rate: '₹40.00',  amount: '₹24,800.00' },
          { name: 'Tracking Charges', note: '620 shipments × ₹5',    qty: '620',  rate: '₹5.00',   amount: '₹3,100.00'  },
        ],
        total: '₹27,900.00',
      },
      convergence: {
        fees: [
          { name: 'Courier Charges',  cycle: 'monthly'   },
          { name: 'Tracking Charges', cycle: 'monthly'   },
          { name: 'Fuel Surcharge',   cycle: 'quarterly' },
        ],
        dates: [
          { label: 'Jan 31', fees: ['Courier Charges', 'Tracking Charges'] },
          { label: 'Feb 28', fees: ['Courier Charges', 'Tracking Charges'] },
          { label: 'Mar 31', fees: ['Courier Charges', 'Tracking Charges', 'Fuel Surcharge'] },
        ],
      },
      convergenceNote: 'Courier Charges and Tracking Charges are billed monthly; Fuel Surcharge quarterly. In January and February they produce separate billing reports. In March all three cycle ends fall on the same date — one report covers all three.',
    },

    rateCardWildcard: {
      exampleBefore: 'zone: "Zone A"',
      migration: {
        beforeDims: ['Zone'],
        newDim: 'Weight',
        cards: [
          { id: 'rc-1', before: ['Zone A'], rate: '₹40 / delivery' },
          { id: 'rc-2', before: ['Zone B'], rate: '₹55 / delivery' },
        ],
      },
      lookup: {
        cards: [
          {
            id: 'rc-1',
            dims: [{ label: 'Zone', value: 'Zone A' }, { label: 'Weight', isAny: true, value: null }],
            rate: '₹40',
            covers: 'Zone A × any weight (fallback)',
          },
          {
            id: 'rc-2',
            dims: [{ label: 'Zone', value: 'Zone A' }, { label: 'Weight', value: '0–0.5 kg' }],
            rate: '₹50',
            covers: 'Zone A × 0–0.5 kg only (overrides above)',
          },
          {
            id: 'rc-3',
            dims: [{ label: 'Zone', value: 'Zone B' }, { label: 'Weight', isAny: true, value: null }],
            rate: '₹60',
            covers: 'Zone B × any weight',
          },
        ],
        queries: [
          { dims: [{ label: 'Zone', value: 'Zone A' }, { label: 'Weight', value: '0–0.5 kg' }], rate: '₹50', matchType: 'specific', reason: 'specific wins' },
          { dims: [{ label: 'Zone', value: 'Zone A' }, { label: 'Weight', value: '0.5–2 kg' }], rate: '₹40', matchType: 'wildcard', reason: 'wildcard fallback' },
          { dims: [{ label: 'Zone', value: 'Zone B' }, { label: 'Weight', value: '0–0.5 kg' }], rate: '₹60', matchType: 'wildcard', reason: 'wildcard fallback' },
        ],
      },
      grid: {
        dims: ['Zone', 'Weight'],
        rows: [
          { cells: [{ value: 'Zone A' }, { isAny: true }], rate: '₹40 / delivery', isWildcard: true },
          { cells: [{ value: 'Zone A' }, { value: '0–0.5 kg' }], rate: '₹50 / delivery' },
          { cells: [{ value: 'Zone B' }, { isAny: true }], rate: '₹55 / delivery', isWildcard: true },
        ],
      },
    },

    rateCardMatrix: {
      feeComponent: 'Courier Charges',
      tagLabels: ['Brand', 'Zone'],
      combinations: [
        { tags: ['Brand-1', 'Zone-A'], type: 'Fixed',    value: '₹5.00 / month' },
        { tags: ['Brand-1', 'Zone-B'], type: 'Fixed',    value: '₹4.00 / month' },
        { tags: ['Brand-2', 'Zone-A'], type: 'Per Unit', value: '₹10 / delivery' },
        { tags: ['Brand-2', 'Zone-B'], type: 'Per Unit', value: '₹8 / delivery'  },
      ],
    },

    multiCurrency: {
      feeComponent: 'Courier Charges',
      tagLabels: ['Brand', 'Zone'],
      tags: ['Brand-1', 'Zone-A'],
      rates: [
        { currency: 'INR', type: 'Fixed',    value: '₹5.00 / month',    note: 'Customers billed in INR' },
        { currency: 'USD', type: 'Fixed',    value: '$0.06 / month',    note: 'Customers billed in USD' },
        { currency: 'AED', type: 'Fixed',    value: 'AED 0.22 / month', note: 'Customers billed in AED' },
      ],
    },

    contractFlow: {
      customers: [
        { id: 'cust-1', name: 'Flipkart Supply Chain Pvt. Ltd.',          taxId: 'GSTIN 27AABCF1234A1Z5', currency: 'INR' },
        { id: 'cust-2', name: 'Amazon Transportation Services Pvt. Ltd.', taxId: 'GSTIN 29AABCA4567B1Z6', currency: 'INR' },
        { id: 'cust-3', name: 'Delhivery Limited',                        taxId: 'GSTIN 07AABCD8901C1Z3', currency: 'INR' },
      ],
      availableTaxes: ['CGST 9%', 'SGST 9%', 'IGST 18%'],
      story: {
        scenario: "It's the first week of Q2. Your sales team just forwarded a signed order form from {customer} — a 12-month agreement for delivery and tracking services starting April 1st. You're the billing admin at Acme Logistics. The services go live in three days. Get this into the system.",
        steps: {
          customer: {
            narrative: "Select the customer this contract is for. Each customer is a distinct legal entity — the name, tax ID, and currency on the contract flow through to every invoice.",
            prompt: "The walkthrough defaults to Flipkart. Select Amazon or Delhivery to see how the same contract structure applies to a different customer — the setup is identical, just different invoice headers.",
          },
          components: {
            narrative: "The order form lists three services: Courier Charges, Fuel Surcharge, and Tracking Charges. {customer}'s ops team requested all three to get full cost visibility broken out on every invoice.",
            prompt: "Try unchecking Fuel Surcharge. High-volume customers sometimes negotiate it out. If you removed it, that line would disappear from every invoice this contract generates.",
          },
          configure: {
            narrative: "The order form also included billing preferences from {customer}'s finance team. They had specific asks on cycles, payment modes, and discounts — and your sales team agreed to most of them.",
            fcNotes: {
              'Courier Charges': "{customer}'s core service. Postpaid and monthly — they want to see actual delivery counts before paying, which is standard for courier billing. The 10% discount for the first 3 months was their ask during negotiation; it shifts to a flat ₹500 credit from period 4.",
              'Fuel Surcharge': "Prepaid and quarterly. Fuel costs are hard to meter in real time, so Acme estimates them at the start of each quarter. The cycle schedule (cycles 3–5) reflects a delayed start — {customer}'s fleet integration doesn't go live until June.",
              'Tracking Charges': "Smaller and predictable. Tax-exempt for this customer — {customer}'s procurement team submitted a tax certificate. No discount either; the standard rate was accepted without pushback.",
            },
            modeSwitch: {
              toPrepaid: "You've switched to prepaid. In this scenario, {customer} would need to commit to expected delivery volumes at the start of each period. The estimates table below is where you'd capture that. Actual usage gets reconciled at period end.",
              toPostpaid: "Back to postpaid — {customer} pays based on what was actually delivered. No upfront estimates needed, and no risk of over- or under-estimating.",
            },
          },
          settings: {
            narrative: "The last page of the order form covers renewal terms and financial guardrails. {customer}'s procurement team was specific: auto-renew on, and a cumulative cap to limit total exposure on both sides.",
            prompt: "Try turning auto-renew off. With it off, someone at Acme would need to manually trigger a renewal before the contract expires — or billing stops and {customer}'s ops team gets an unpleasant surprise.",
          },
          summary: {
            closing: "This contract is ready to activate. From April 1st, the billing engine starts tracking delivery events. At the end of each month, Courier Charges and Tracking invoices go out. Quarterly, a Fuel Surcharge invoice follows based on the prepaid estimate. The 5% post-tax discount applies across the board.",
          },
        },
      },
    },

    contract: {
      customer: 'Flipkart Supply Chain Pvt. Ltd.',
      product: 'Logistics-as-a-Service',
      currency: '₹',
      autoRenew: true,
      contractDiscount: { type: 'percentage', value: 5, timing: 'post-tax' },
      contractMinMax: { min: null, max: 15000000 },
      billingPeriodMinMax: {
        min: 10000,
        max: null,
        includedComponents: ['Courier Charges', 'Fuel Surcharge'],
      },
      contractStart: '1 Apr 2024',
      feeComponents: [
        {
          name: 'Courier Charges',
          paymentMode: 'postpaid',
          billingCycle: 'Monthly',
          schedule: { mode: 'date', start: '1 Apr 2024', end: null },
          taxes: ['CGST 9%', 'SGST 9%'],
          discounts: [
            { fromPeriod: 1, toPeriod: 3,    type: 'percentage', value: 10 },
            { fromPeriod: 4, toPeriod: null, type: 'fixed',      value: 500 },
          ],
        },
        {
          name: 'Fuel Surcharge',
          paymentMode: 'prepaid',
          billingCycle: 'Quarterly',
          schedule: { mode: 'cycle', fromCycle: 3, toCycle: 5, resolvedStart: '1 Jun 2024', resolvedEnd: '31 Aug 2024' },
          taxes: ['IGST 18%'],
          estimates: [
            { usageAttribute: 'Distance', tags: 'Zone A', value: '5,000 km' },
            { usageAttribute: 'Distance', tags: 'Zone B', value: '3,000 km' },
          ],
          discounts: [],
        },
        {
          name: 'Tracking Charges',
          paymentMode: 'postpaid',
          billingCycle: 'Monthly',
          schedule: null,
          taxes: [],
          discounts: [],
        },
      ],
    },

    customer: {
      name: 'Flipkart Supply Chain Pvt. Ltd.',
      taxIdLabel: 'GSTIN',
      taxId: '27AABCF1234A1Z5',
      currency: 'INR',
      currencySymbol: '₹',
      billingAddress: {
        line1: '15th Floor, Marathon Futurex',
        city: 'Mumbai',
        state: 'Maharashtra',
        country: 'India',
      },
      shippingAddress: {
        line1: 'Gat No. 144, Village Talawade',
        city: 'Pune',
        state: 'Maharashtra',
        country: 'India',
      },
      taxNote: 'Intrastate supply — both addresses are in Maharashtra. CGST + SGST applies.',
      rateCardCurrencies: [
        { currency: 'INR', note: 'Customers billed in INR', active: true },
        { currency: 'USD', note: 'Customers billed in USD', active: false },
        { currency: 'AED', note: 'Customers billed in AED', active: false },
      ],
    },

    rateCard: {
      currency: '₹',
      feeComponent: 'Courier Charges',
      unit: 'deliveries',
      fixed: { price: 500 },
      ratePerUnit: {
        rate: 10,
        examples: [
          { usage: 42, price: 420 },
          { usage: 89, price: 890 },
        ],
      },
      tiers: [
        { start: 0,   limit: 50,   rate: 10 },
        { start: 50,  limit: 100,  rate: 9  },
        { start: 100, limit: null, rate: 8  },
      ],
      graduated: {
        examples: [
          { usage: 40,  price: 400,  breakdown: '10 × 40' },
          { usage: 60,  price: 590,  breakdown: '10 × 50 + 9 × 10' },
          { usage: 120, price: 1110, breakdown: '10 × 50 + 9 × 50 + 8 × 20' },
        ],
      },
      volume: {
        examples: [
          { usage: 40,  price: 400, breakdown: '10 × 40  (0–50 tier)' },
          { usage: 60,  price: 540, breakdown: '9 × 60   (50–100 tier)' },
          { usage: 120, price: 960, breakdown: '8 × 120  (100+ tier)' },
        ],
      },
      minMax: {
        defaultFlatFee: 300,
        defaultRate: 8,
        defaultVariant: 'max',
        examples: [
          { usage: 30,  flatFee: 300, calculated: 240, calcNote: '30 × 8',  logic: 'Max', price: 300 },
          { usage: 60,  flatFee: 300, calculated: 480, calcNote: '60 × 8',  logic: 'Max', price: 480 },
          { usage: 100, flatFee: 600, calculated: 700, calcNote: '100 × 7', logic: 'Min', price: 600 },
        ],
      },
      timeBased: {
        feeComponent: 'Courier Charges',
        unit: 'delivery',
        slot: 'Courier Charges · Zone-A · INR',
        standardRateLabel: '₹10 / delivery',
        override: {
          rateLabel: '₹8 / delivery',
          window: 'Nov 1 – Nov 30, 2026',
          name: 'Diwali Promotion',
        },
        variant: {
          name: 'RC_002 (Negotiated)',
          rateLabel: '₹7 / delivery',
        },
        periodTable: [
          { period: 'Oct 1 – Oct 31', window: 'Nov 1 – Nov 30', rate: '₹10 / delivery', reason: 'period ends before window' },
          { period: 'Nov 1 – Nov 30', window: 'Nov 1 – Nov 30', rate: '₹8 / delivery',  reason: 'period ends within window', active: true },
          { period: 'Dec 1 – Dec 31', window: 'Nov 1 – Nov 30', rate: '₹10 / delivery', reason: 'period ends after window' },
          { period: 'Oct 15 – Nov 14',window: 'Nov 1 – Nov 30', rate: '₹8 / delivery',  reason: 'period ends within window', active: true },
        ],
      },
    },
  },

  {
    id: 'saas',
    label: 'SaaS',

    product: {
      companyName: 'TechCorp',
      products: ['API Platform', 'Analytics Suite', 'Developer Tools'],
      primaryProduct: 'API Platform',
      singleTagExample: {
        tagDim: 'Region',
        tagValues: ['us-east-1', 'ap-south-1', 'eu-west-1'],
        feeComponents: [
          { name: 'API Read Calls',  unit: 'K calls', rates: ['$0.008', '$0.010', '$0.009'] },
          { name: 'API Write Calls', unit: 'K calls', rates: ['$0.040', '$0.050', '$0.045'] },
          { name: 'Data Storage',    unit: 'GB',      rates: ['$0.10',  '$0.12',  '$0.11']  },
        ],
      },
      multiTagExample: {
        tags: [
          { label: 'Region', values: ['us-east-1', 'ap-south-1'] },
          { label: 'Plan',   values: ['Pro', 'Enterprise']        },
        ],
      },
      tree: [
        { name: 'API Platform',    tags: ['Region', 'Plan'], components: ['API Read Calls', 'API Write Calls', 'Data Storage']         },
        { name: 'Analytics Suite', tags: ['Plan'],           components: ['Dashboard Views', 'Report Exports', 'Data Connectors']      },
        { name: 'Developer Tools', tags: ['Region'],         components: ['CI/CD Minutes', 'Artifact Storage', 'Deployment Units']     },
      ],
      usageEvent: {
        product: 'API Platform',
        tags: 'Region = us-east-1, Plan = Pro',
        feeComponent: 'API Read Calls',
        quantity: '500,000 calls',
      },
    },

    feeComponent: {
      examples: ['API Read Calls', 'API Write Calls', 'Data Storage'],
      invoice: {
        id: 'INV-2024-0892',
        due: 'Due Feb 28, 2024',
        rows: [
          { accent: 'purple', name: 'API Read Calls',  note: '500K calls',  qty: '500,000', rate: '$0.001', amount: '$500.00' },
          { accent: 'teal',   name: 'API Write Calls', note: '100K calls',  qty: '100,000', rate: '$0.005', amount: '$500.00' },
          { accent: 'amber',  name: 'Data Storage',    note: '150 GB used', qty: '150',     rate: '$0.10',  amount: '$15.00'  },
        ],
        total: '$1,015.00',
      },
    },

    rateAttributes: {
      useCases: [
        ['Regional API pricing',     'Region',         'us-east-1, ap-south-1, eu-west-1'],
        ['Per-plan pricing',         'Plan',           'Starter, Pro, Enterprise'],
        ['Region + plan tier',       'Region, Plan',   'us-east-1 × Pro …'],
        ['Feature-based pricing',    'Feature',        'Analytics, Exports, SSO'],
      ],
      singleDim: {
        intro: 'A cloud API provider charging different rates by region defines a Region dimension on the product:',
        headers: ['Region'],
        rows: [['us-east-1'], ['ap-south-1'], ['eu-west-1']],
        outro: 'Fee components that use Region are priced once per region. Each carries its own per-call rate in the pricing plan.',
        dimLabel: 'Region',
      },
      multiDim: {
        intro: 'A platform pricing by both region and subscription plan defines Region and Plan on the product:',
        headers: ['Region', 'Plan'],
        rows: [
          ['us-east-1', 'Pro'],
          ['us-east-1', 'Enterprise'],
          ['ap-south-1', 'Pro'],
          ['ap-south-1', 'Enterprise'],
        ],
        dims: [
          { key: 'region', label: 'Region', values: ['us-east-1', 'ap-south-1'] },
          { key: 'plan',   label: 'Plan',   values: ['Pro', 'Enterprise']        },
        ],
      },
      partialTags: {
        productDims: [
          { key: 'region',  label: 'Region',  values: ['us-east-1', 'ap-south-1'] },
          { key: 'plan',    label: 'Plan',    values: ['Pro', 'Enterprise'] },
          { key: 'feature', label: 'Feature', values: ['Analytics', 'Exports'] },
        ],
        components: [
          { name: 'API Read Calls',  uses: ['region', 'plan'] },
          { name: 'API Write Calls', uses: ['region', 'plan'] },
          { name: 'Data Storage',    uses: ['region'] },
        ],
      },
      invoiceBehavior: 'A SaaS customer with API usage across two regions sees two API Calls lines — one per region — each billed at that region\'s rate.',
    },

    billableMetric: {
      product: 'API Platform',
      currentFlow: {
        feeComponent: 'API Access Fee',
        usageAttribute: 'API Calls (K)',
        unit: 'thousand calls',
        groups: [
          { refId: 'REQ_1', calc: '10',  total: 10  },
          { refId: 'REQ_2', calc: '50',  total: 50  },
          { refId: 'REQ_3', calc: '5',   total: 5   },
          { refId: 'REQ_4', calc: '100', total: 100 },
        ],
        finalSum: 165,
        rateCardLabel: '$0.50 per 1,000 calls (flat)',
        rateCardCalc: '165 × $0.50 = $82.50',
        invoiceLine: 'API Access Fee',
        charge: '$82.50',
        transitionNote: 'Notice what happened at step 3 — REQ_3 (a staging call) is summed into the total alongside production traffic. This works if every call should be billed. But if you only want to charge for production requests, there is no way to express that in this flow.',
      },
      problems: [
        { title: 'Charging a subset of events',          desc: 'The sum includes staging and production calls alike. If API Access Fee applies only to production calls, there is no way to exclude staging events from the sum.' },
        { title: 'Recorded unit ≠ rate card unit',        desc: 'If each batch request processes 10× the data of a sync request, the raw call count going into the rate card understates actual volume by a factor of 10.' },
        { title: 'Different formula per event type',      desc: 'If sync calls and batch calls are priced on different schedules, a single sum of all API calls cannot route each type to its own rate.' },
        { title: 'Threshold charging with accurate counts', desc: 'A zero-rate tier workaround produces the right total, but the invoice shows all 4 requests billed — customers expect to see only the requests that exceeded the SLA threshold.' },
      ],
      filterExamples:     ['Environment = production', 'Request Type = batch', 'Response Time > 200'],
      expressionExamples: ['API Calls × 10', 'API Calls', 'Response Time'],
      eventCols: [
        { key: 'refId',       label: 'Request ID',         type: 'ref'     },
        { key: 'apiCalls',    label: 'API Calls (K)',       type: 'usage'   },
        { key: 'respTime',    label: 'Response Time (ms)',  type: 'usage'   },
        { key: 'requestType', label: 'Request Type',        type: 'product' },
        { key: 'environment', label: 'Environment',         type: 'product' },
      ],
      events: [
        { refId: 'REQ_1', apiCalls: 10,  respTime: 120, requestType: 'sync',  environment: 'production' },
        { refId: 'REQ_2', apiCalls: 50,  respTime: 340, requestType: 'batch', environment: 'production' },
        { refId: 'REQ_3', apiCalls: 5,   respTime: 95,  requestType: 'sync',  environment: 'staging'    },
        { refId: 'REQ_4', apiCalls: 100, respTime: 280, requestType: 'batch', environment: 'production' },
      ],
      filterScenario: {
        fee: 'API Access Fee',
        problem: 'REQ_3 is a staging call and should not be billed. Without a filter, all 165K calls are summed — including the 5K staging call — inflating the billable quantity.',
        metricName: 'Production API Calls',
        filter: 'Environment = production',
        expression: 'API Calls',
        aggregation: 'Sum',
        before: '165K calls (staging call included)',
        after:  '160K calls (production only)',
      },
      transformScenario: {
        fee: 'Batch Processing Fee',
        problem: 'Each batch request processes 10× the data of a sync request. The rate card should receive an adjusted count reflecting real volume, not the raw recorded count.',
        metricName: 'Weighted Batch Calls',
        filter: 'Request Type = batch',
        expression: 'API Calls × 10',
        aggregation: 'Sum',
        before: '150K batch calls piped as-is — volume underrepresented',
        after:  '1,500K adjusted calls piped to rate card — correct volume weighting',
      },
      conditionalScenario: {
        fee: 'API Access Fee',
        problem: 'Sync and batch requests are priced on separate schedules. A single meter cannot route each type to a different rate card.',
        metrics: [
          { name: 'Sync API Calls',  filter: 'Request Type = sync',  expression: 'API Calls', aggregation: 'Sum', result: '10 + 5 = 15K calls' },
          { name: 'Batch API Calls', filter: 'Request Type = batch', expression: 'API Calls', aggregation: 'Sum', result: '50 + 100 = 150K calls' },
        ],
      },
      thresholdScenario: {
        fee: 'Latency SLA Fee',
        problem: 'An SLA penalty applies only to requests with response time above 200ms. A rate card workaround shows all 4 requests billed — customers expect to see only the 2 breaching requests.',
        metricName: 'SLA-Breach Calls',
        filter: 'Response Time > 200',
        expression: 'API Calls',
        aggregation: 'Sum',
        before: '4 requests in report — correct total, but wrong count confuses customers',
        after:  '2 requests: REQ_2 (340ms) and REQ_4 (280ms) — count and value both correct',
      },
      metrics: [
        { name: 'Production API Calls', fee: 'API Access Fee',       filter: 'Environment = production',  expression: 'API Calls',       aggregation: 'Sum' },
        { name: 'Sync API Calls',       fee: 'API Access Fee',       filter: 'Request Type = sync',       expression: 'API Calls',       aggregation: 'Sum' },
        { name: 'Batch API Calls',      fee: 'Batch Processing Fee', filter: 'Request Type = batch',      expression: 'API Calls × 10',  aggregation: 'Sum' },
      ],
    },

    plan: {
      currency: '₹',
      plans: [
        {
          name: 'Annual Plan',
          rateCard: 10000,
          duration: '1 year',
          fees: [
            {
              product: 'Email',
              name: 'Email Fee',
              paymentMode: 'prepaid',
              startsAt: 'With contract',
              duration: '1 year',
              billingCycle: '1 year',
              grant: { type: 'Quantity-only', amount: 1000, unit: 'emails' },
              overageRate: '₹1 / email',
              overageRateNum: 1,
            },
            {
              product: 'SMS',
              name: 'SMS Fee',
              paymentMode: 'prepaid',
              startsAt: 'With contract',
              duration: '1 year',
              billingCycle: '1 year',
              grant: { type: 'Quantity-only', amount: 100, unit: 'SMS' },
              overageRate: '₹2 / SMS',
              overageRateNum: 2,
            },
          ],
        },
        {
          name: 'Annual Plan',
          rateCard: 10000,
          duration: '1 year',
          fees: [
            {
              product: 'Email',
              name: 'Email Fee',
              paymentMode: 'prepaid',
              startsAt: 'With contract',
              duration: '1 year',
              billingCycle: '1 month',
              grant: { type: 'Quantity-only', amount: 1200, unit: 'emails' },
              perCycleGrantLimit: 100,
              overageRate: '₹1 / email',
              overageRateNum: 1,
            },
            {
              product: 'SMS',
              name: 'SMS Fee',
              paymentMode: 'prepaid',
              startsAt: 'With contract',
              duration: '1 year',
              billingCycle: '1 month',
              grant: { type: 'Quantity-only', amount: 120, unit: 'SMS' },
              perCycleGrantLimit: 10,
              overageRate: '₹2 / SMS',
              overageRateNum: 2,
            },
          ],
        },
      ],
      overageExample: {
        plan: 'Annual Plan',
        planPrice: 10000,
        fee: 'Email Fee',
        included: 1000,
        used: 1340,
        overage: 340,
        overageRate: 1,
        overageCharge: 340,
        unit: 'emails',
        otherFee: { name: 'SMS Fee', used: 80, included: 100, overageCharge: 0 },
      },
      contractExample: {
        customer: 'Stripe, Inc.',
        plans: [
          { name: 'Annual Plan', price: '₹10,000' },
        ],
        overages: [
          { fee: 'Email Fee (overage)', note: '340 excess emails × ₹1', charge: '₹340' },
        ],
        adHocFees: [
          { name: 'WhatsApp Fee', note: '200 messages × ₹3', charge: '₹600' },
        ],
        total: '₹10,940',
      },
      carryForward: {
        fee: 'Email Fee',
        included: 1000,
        unit: 'emails',
        cycles: [
          { label: 'Q1', used: 720,  carried: 280, balance: 1280 },
          { label: 'Q2', used: 1100, carried: 180, balance: 1180 },
          { label: 'Q3', used: 1340, carried: 0,   balance: 1000, overageNote: '160 at ₹1 = ₹160' },
        ],
      },
      carryForwardLimited: {
        cfUntilPeriod: 6,
        fees: [
          { name: 'Email Fee', included: 100, unit: 'emails', overageRateNum: 1, overageRate: '₹1 / email' },
          { name: 'SMS Fee',   included: 10,  unit: 'SMS',    overageRateNum: 2, overageRate: '₹2 / SMS'   },
        ],
        // default usages chosen to show: quiet early months build carry buffer,
        // buffer is LOST at month-6 cutoff, month-7 spike hits overage
        defaultUsages: {
          'Email Fee': [80, 90, 110, 70,  95, 105, 120, 90],
          'SMS Fee':   [8,  9,  11,  7,   10,  10,  12,  9],
        },
      },
    },

    grant: {
      product: 'API Platform',
      name: 'Launch Free Period',
      description: 'Zero-rates API Access Fee for the first 30 days or up to 10,000 API calls, whichever is exhausted first.',
      type: 'both',
      feeComponent: 'API Access Fee',
      duration: { days: 30 },
      quantity: { driver: 'API Calls', amount: 10000, unit: 'calls' },
      expiryDate: null,
      race: {
        daysElapsed: 18, totalDays: 30,
        callsUsed: 10000, totalCalls: 10000,
        unit: 'calls',
        exhaustedBy: 'quantity',
      },
      scopeTable: [
        { fee: 'API Access Fee',       driver: 'API Calls',          granted: true,  note: 'Grant applies — zero-rated for duration or up to 10,000 calls' },
        { fee: 'Batch Processing Fee', driver: 'API Calls',          granted: false, note: 'Not in grant scope — billed at full rate'                      },
        { fee: 'Latency SLA Fee',      driver: 'Response Time (ms)', granted: false, note: 'Different meter'                                     },
      ],
      billLines: [
        { name: 'Launch Free Period', isGrant: true, qty: '10,000 calls', rate: '₹0', amount: '₹0' },
      ],
    },

    productAttribute: {
      product: 'API Platform',
      description: 'Extra context recorded with each API call event — not used in pricing, but available for analytics and debugging.',
      attributes: [
        { name: 'Request Type',  type: 'Enum',   values: 'sync, async, batch',              description: 'How the call was initiated' },
        { name: 'Client SDK',    type: 'Enum',   values: 'python, javascript, go, java',     description: 'SDK version used by the caller' },
        { name: 'Environment',   type: 'Enum',   values: 'production, staging',              description: 'Whether the call was made in production or a test environment' },
      ],
      sampleTable: {
        usageAttrs: ['API Calls (K)', 'Response Time (ms)', 'Data Transfer (MB)'],
        productAttrs: ['Request Type'],
        rows: [
          { refId: 'REQ_1', usageVals: ['10',  '120', '5'],   productVals: ['sync']  },
          { refId: 'REQ_2', usageVals: ['50',  '340', '22'],  productVals: ['batch'] },
          { refId: 'REQ_3', usageVals: ['5',   '95',  '2'],   productVals: ['async'] },
          { refId: 'REQ_4', usageVals: ['100', '280', '45'],  productVals: ['batch'] },
        ],
      },
    },

    usageAttribute: {
      examples: [
        {
          feeComponent: 'Storage Charges',
          attribute: 'Storage Used',
          unit: 'GB',
          description: 'The system records how many GB a customer consumed each month. That measurement drives the Storage Charges line. The customer sees the charge — not "Storage Used = 47 GB".',
        },
        {
          feeComponent: 'Compute Fee',
          attribute: 'CPU Hours',
          unit: 'vCPU-hours',
          description: 'The system records compute time consumed. That drives the Compute Fee charge. Switching to memory-based pricing later means adding a new attribute and remapping — the invoice label stays unchanged.',
        },
      ],
      oneToMany: [
        { attribute: 'API Calls',    feeComponents: ['Read Fee', 'Compute Fee'] },
        { attribute: 'Storage Used', feeComponents: ['Storage Charges', 'Backup Charges'] },
        { attribute: 'Data Transfer', feeComponents: ['Bandwidth Fee', 'CDN Fee'] },
      ],
      manyToOne: {
        feeComponent: 'Platform Fee',
        attributes: ['API Calls', 'Storage Used'],
      },
      multipliers: [
        { attribute: 'API Calls',          multiplier: '0.001',   feeComponent: 'Compute Charge ($)',  effect: '1 call → $0.001' },
        { attribute: 'Data Transfer (MB)', multiplier: '0.00098', feeComponent: 'Bandwidth Fee (GB)',  effect: '1 MB → ~0.001 GB' },
        { attribute: 'Storage Used (GB)',  multiplier: '0.033',   feeComponent: 'Daily Storage ($)',   effect: '1 GB/month → daily rate' },
      ],
    },

    usageGrouping: {
      feeComponent: 'API Read Calls',
      tiersNote: 'Tier 1: 0–500K calls → $0.001 each   ·   Tier 2: 500K+ calls → $0.0008 each',
      records: [
        { qty: '300K calls' },
        { qty: '400K calls' },
      ],
      separate: {
        refIds: ['batch-morning', 'batch-afternoon'],
        results: [
          { calc: '300K × $0.001  (Tier 1)', charge: '$300' },
          { calc: '400K × $0.001  (Tier 1)', charge: '$400' },
        ],
        total: '$700',
      },
      grouped: {
        refId: 'jan-14-api',
        aggregatedQty: '700K calls',
        tiers: [
          { calc: '500K × $0.001  (Tier 1)', charge: '$500' },
          { calc: '200K × $0.0008 (Tier 2)', charge: '$160' },
        ],
        total: '$660',
      },
    },

    usage: {
      product: 'API Platform',
      customer: 'Stripe, Inc.',
      dedup: {
        batchId: 'batch-jan-14-api',
        attr: 'API Calls',
        first:  { value: '1,000 calls', note: 'sent at 3:17 PM — pipeline reported partial count' },
        second: { value: '1,200 calls', note: 'sent at 3:31 PM — corrected after pipeline flush' },
      },
      usageAttr: { label: 'API Calls', value: '1,000', unit: 'calls' },
      rateAttrs: [{ label: 'Region', value: 'us-east-1' }, { label: 'Plan', value: 'Pro' }],
      feeComponent: 'API Read Calls',
      timestamp: '14 Jan 2025, 03:17 PM',
      billingCycle: { start: '1 Jan 2025', end: '31 Jan 2025' },
      cycleNote: 'This usage falls inside the January billing cycle and will be accumulated toward the January invoice for Stripe.',
      partialIngestion: {
        rateAttr: { label: 'Region', value: 'us-east-1' },
        contracts: [
          { label: 'Contract A', feeComponent: 'API Read Calls',  usageAttr: 'API Calls' },
          { label: 'Contract B', feeComponent: 'Storage Charges', usageAttr: 'Storage Used' },
        ],
        measurements: [
          { attr: 'API Calls',    value: '1,000 calls', matched: true,  reason: 'Rate card for Region = us-east-1 found in Contract A' },
          { attr: 'Storage Used', value: '50 GB',       matched: false, reason: 'No rate card for Region = us-east-1 in Contract B' },
        ],
      },
    },

    proration: {
      fixedFee: 99,
      compositeFixed: 50,
      compositeRate: 8,
      compositeUnit: 'thousand calls',
      compositeUnitPlural: 'thousand calls',
    },

    billingReport: {
      report: {
        reportId: 'BR-2025-0412',
        customer: 'Stripe, Inc.',
        period: { start: '1 Jan 2025', end: '31 Jan 2025' },
        rows: [
          { name: 'API Read Calls',  note: '12,500K calls × $0.008',  qty: '12,500K', rate: '$0.008/K', amount: '$100.00' },
          { name: 'API Write Calls', note: '3,200K calls × $0.040',   qty: '3,200K',  rate: '$0.040/K', amount: '$128.00' },
        ],
        total: '$228.00',
      },
      convergence: {
        fees: [
          { name: 'API Read Calls',  cycle: 'monthly'   },
          { name: 'API Write Calls', cycle: 'monthly'   },
          { name: 'Data Storage',    cycle: 'quarterly' },
        ],
        dates: [
          { label: 'Jan 31', fees: ['API Read Calls', 'API Write Calls'] },
          { label: 'Feb 28', fees: ['API Read Calls', 'API Write Calls'] },
          { label: 'Mar 31', fees: ['API Read Calls', 'API Write Calls', 'Data Storage'] },
        ],
      },
      convergenceNote: 'API Read Calls and API Write Calls are billed monthly; Data Storage quarterly. In January and February they generate separate billing reports. In March all three cycles end on the same date — one report covers all three.',
    },

    rateCardWildcard: {
      exampleBefore: 'region: "us-east-1"',
      migration: {
        beforeDims: ['Region'],
        newDim: 'Plan',
        cards: [
          { id: 'rc-1', before: ['us-east-1'],  rate: '$8 / K calls' },
          { id: 'rc-2', before: ['ap-south-1'], rate: '$10 / K calls' },
        ],
      },
      lookup: {
        cards: [
          {
            id: 'rc-1',
            dims: [{ label: 'Region', value: 'us-east-1' }, { label: 'Plan', isAny: true, value: null }],
            rate: '$8/K',
            covers: 'us-east-1 × any plan (fallback)',
          },
          {
            id: 'rc-2',
            dims: [{ label: 'Region', value: 'us-east-1' }, { label: 'Plan', value: 'Enterprise' }],
            rate: '$6/K',
            covers: 'us-east-1 × Enterprise only (overrides above)',
          },
          {
            id: 'rc-3',
            dims: [{ label: 'Region', value: 'ap-south-1' }, { label: 'Plan', isAny: true, value: null }],
            rate: '$10/K',
            covers: 'ap-south-1 × any plan',
          },
        ],
        queries: [
          { dims: [{ label: 'Region', value: 'us-east-1' }, { label: 'Plan', value: 'Enterprise' }], rate: '$6/K',  matchType: 'specific', reason: 'specific wins' },
          { dims: [{ label: 'Region', value: 'us-east-1' }, { label: 'Plan', value: 'Pro' }],        rate: '$8/K',  matchType: 'wildcard', reason: 'wildcard fallback' },
          { dims: [{ label: 'Region', value: 'ap-south-1' }, { label: 'Plan', value: 'Pro' }],       rate: '$10/K', matchType: 'wildcard', reason: 'wildcard fallback' },
        ],
      },
      grid: {
        dims: ['Region', 'Plan'],
        rows: [
          { cells: [{ value: 'us-east-1' }, { isAny: true }], rate: '$8 / K calls', isWildcard: true },
          { cells: [{ value: 'us-east-1' }, { value: 'Enterprise' }], rate: '$6 / K calls' },
          { cells: [{ value: 'ap-south-1' }, { isAny: true }], rate: '$10 / K calls', isWildcard: true },
        ],
      },
    },

    rateCardMatrix: {
      feeComponent: 'API Read Calls',
      tagLabels: ['Region', 'Plan'],
      combinations: [
        { tags: ['us-east-1',  'Pro'],        type: 'Per Unit', value: '$8 / K calls'  },
        { tags: ['us-east-1',  'Enterprise'], type: 'Per Unit', value: '$6 / K calls'  },
        { tags: ['ap-south-1', 'Pro'],        type: 'Per Unit', value: '$10 / K calls' },
        { tags: ['ap-south-1', 'Enterprise'], type: 'Per Unit', value: '$8 / K calls'  },
      ],
    },

    multiCurrency: {
      feeComponent: 'API Read Calls',
      tagLabels: ['Region', 'Plan'],
      tags: ['us-east-1', 'Pro'],
      rates: [
        { currency: 'USD', type: 'Per Unit', value: '$8 / K calls',  note: 'Customers billed in USD' },
        { currency: 'EUR', type: 'Per Unit', value: '€7 / K calls',  note: 'Customers billed in EUR' },
        { currency: 'GBP', type: 'Per Unit', value: '£6 / K calls',  note: 'Customers billed in GBP' },
      ],
    },

    contractFlow: {
      customers: [
        { id: 'cust-1', name: 'Stripe, Inc.',          taxId: 'EIN 91-0891514', currency: 'USD' },
        { id: 'cust-2', name: 'Brex Inc.',             taxId: 'EIN 83-2707815', currency: 'USD' },
        { id: 'cust-3', name: 'Mercury Financial LLC', taxId: 'EIN 47-3561414', currency: 'USD' },
      ],
      availableTaxes: ['Sales Tax 8.5%', 'VAT 20%'],
      story: {
        scenario: "{customer} has been on TechCorp's pay-as-you-go plan for six months. Their API call volume has grown 4× and their CFO wants predictable billing. {customer}'s procurement team just sent over a signed contract request — committed usage in exchange for better rates. You're setting up their first committed contract.",
        steps: {
          customer: {
            narrative: "Select the customer this contract is for. Each customer is a distinct legal entity — USD billing, EIN on file from the pay-as-you-go account. Unlike the previous arrangement, invoices now go to their finance team for net-30 payment instead of auto-charging a card.",
            prompt: "The walkthrough defaults to Stripe. Switch to Brex or Mercury to see how the same contract applies to a different fintech customer. Same product, same structure, different entity on the invoice.",
          },
          components: {
            narrative: "{customer}'s engineering team wants API Read Calls and API Write Calls under this contract. Data Storage was discussed but their team wants to see one month of usage before committing — they'll revisit it at the 6-month mark.",
            prompt: "Add Data Storage back in. It's set to start from cycle 2, which is the delayed start {customer} requested. They want one month of baseline data before storage billing kicks in.",
          },
          configure: {
            narrative: "{customer}'s engineering lead had specific asks: monthly billing aligned to their own customer cycle, and an aggressive discount for the first 6 months — their way of switching from a competitor mid-year.",
            fcNotes: {
              'API Read Calls': "High volume, postpaid, monthly. The 15% discount for the first 6 months was the hook that closed the deal — TechCorp accepted it as a cost-of-acquisition investment. It steps down to 5% from month 7.",
              'API Write Calls': "Also postpaid and monthly. Note the cycle schedule: cycles 1–6 only. This is a 6-month pilot scope — Write Calls is the service {customer} is evaluating before committing to the full annual term.",
              'Data Storage': "Currently excluded, but set to start from cycle 2 if included. Prepaid and quarterly — {customer} would estimate their storage needs at the start of each quarter.",
            },
            modeSwitch: {
              toPrepaid: "Switching to prepaid would mean {customer} commits to estimated API call volumes at the start of each month. That's unusual for API billing — usage spikes around product launches and can't be predicted reliably. Their engineering lead would push back on this.",
              toPostpaid: "Postpaid is the right fit. {customer}'s call volume varies by product cycle, so they pay for what they actually used at the end of each month.",
            },
          },
          settings: {
            narrative: "{customer}'s legal team was clear: no auto-renew. Their procurement policy requires explicit sign-off each term. But their CFO agreed to a monthly minimum — it gives TechCorp revenue predictability in exchange for the locked-in discount.",
            prompt: "Try turning auto-renew on. With it on, the contract rolls over without anyone taking action — {customer}'s legal team explicitly rejected this. Flip it back off to match their requirement.",
          },
          summary: {
            closing: "Contract ready. From the activation date, TechCorp's billing engine tracks every API call. {customer} gets a monthly invoice — $200 pre-tax discount applied, $100 monthly floor enforced. At the 6-month mark, both teams will review whether to add Data Storage and extend Write Calls.",
          },
        },
      },
    },

    contract: {
      customer: 'Stripe, Inc.',
      product: 'API Platform',
      currency: '$',
      autoRenew: false,
      contractDiscount: { type: 'fixed', value: 200, timing: 'pre-tax' },
      contractMinMax: { min: 1000, max: null },
      billingPeriodMinMax: {
        min: 100,
        max: 5000,
        includedComponents: ['API Read Calls', 'API Write Calls'],
      },
      contractStart: null,
      feeComponents: [
        {
          name: 'API Read Calls',
          paymentMode: 'postpaid',
          billingCycle: 'Monthly',
          schedule: null,
          taxes: ['Sales Tax 8.5%'],
          discounts: [
            { fromPeriod: 1, toPeriod: 6,    type: 'percentage', value: 15 },
            { fromPeriod: 7, toPeriod: null, type: 'percentage', value: 5 },
          ],
        },
        {
          name: 'API Write Calls',
          paymentMode: 'postpaid',
          billingCycle: 'Monthly',
          schedule: { mode: 'cycle', fromCycle: 1, toCycle: 6, resolvedStart: null, resolvedEnd: null },
          taxes: ['Sales Tax 8.5%'],
          discounts: [],
        },
        {
          name: 'Data Storage',
          paymentMode: 'prepaid',
          billingCycle: 'Quarterly',
          schedule: { mode: 'cycle', fromCycle: 2, toCycle: null, resolvedStart: null, resolvedEnd: null },
          taxes: [],
          estimates: [
            { usageAttribute: 'Storage GB', tags: 'us-east-1 / Pro',  value: '500 GB' },
            { usageAttribute: 'Storage GB', tags: 'eu-west-1 / Pro',  value: '200 GB' },
          ],
          discounts: [
            { fromPeriod: 1, toPeriod: null, type: 'fixed', value: 50 },
          ],
        },
      ],
    },

    customer: {
      name: 'Stripe, Inc.',
      taxIdLabel: 'EIN',
      taxId: '91-0891514',
      currency: 'USD',
      currencySymbol: '$',
      billingAddress: {
        line1: '354 Oyster Point Blvd',
        city: 'South San Francisco',
        state: 'California',
        country: 'USA',
      },
      shippingAddress: {
        line1: '354 Oyster Point Blvd',
        city: 'South San Francisco',
        state: 'California',
        country: 'USA',
      },
      taxNote: 'Billing and shipping address are identical — domestic US supply.',
      rateCardCurrencies: [
        { currency: 'USD', note: 'Customers billed in USD', active: true },
        { currency: 'EUR', note: 'Customers billed in EUR', active: false },
        { currency: 'GBP', note: 'Customers billed in GBP', active: false },
      ],
    },

    rateCard: {
      currency: '$',
      feeComponent: 'API Read Calls',
      unit: 'thousand calls',
      fixed: { price: 99 },
      ratePerUnit: {
        rate: 8,
        examples: [
          { usage: 42, price: 336 },
          { usage: 89, price: 712 },
        ],
      },
      tiers: [
        { start: 0,   limit: 50,   rate: 10 },
        { start: 50,  limit: 100,  rate: 8  },
        { start: 100, limit: null, rate: 6  },
      ],
      graduated: {
        examples: [
          { usage: 40,  price: 400,  breakdown: '10 × 40' },
          { usage: 60,  price: 580,  breakdown: '10 × 50 + 8 × 10' },
          { usage: 120, price: 1020, breakdown: '10 × 50 + 8 × 50 + 6 × 20' },
        ],
      },
      volume: {
        examples: [
          { usage: 40,  price: 400, breakdown: '10 × 40   (0–50 tier)' },
          { usage: 60,  price: 480, breakdown: '8 × 60    (50–100 tier)' },
          { usage: 120, price: 720, breakdown: '6 × 120   (100+ tier)' },
        ],
      },
      minMax: {
        defaultFlatFee: 200,
        defaultRate: 9,
        defaultVariant: 'max',
        examples: [
          { usage: 30,  flatFee: 200, calculated: 270, calcNote: '30 × 9',  logic: 'Max', price: 270 },
          { usage: 70,  flatFee: 200, calculated: 630, calcNote: '70 × 9',  logic: 'Max', price: 630 },
          { usage: 100, flatFee: 800, calculated: 600, calcNote: '100 × 6', logic: 'Min', price: 600 },
        ],
      },
      timeBased: {
        feeComponent: 'API Read Calls',
        unit: 'K calls',
        slot: 'API Read Calls · us-east-1 · USD',
        standardRateLabel: '$8 / K calls',
        override: {
          rateLabel: '$6 / K calls',
          window: 'Dec 1 – Dec 31, 2026',
          name: 'Year-End Promotion',
        },
        variant: {
          name: 'RC_002 (Enterprise)',
          rateLabel: '$5 / K calls',
        },
        periodTable: [
          { period: 'Nov 1 – Nov 30', window: 'Dec 1 – Dec 31', rate: '$8 / K calls', reason: 'period ends before window' },
          { period: 'Dec 1 – Dec 31', window: 'Dec 1 – Dec 31', rate: '$6 / K calls', reason: 'period ends within window', active: true },
          { period: 'Jan 1 – Jan 31', window: 'Dec 1 – Dec 31', rate: '$8 / K calls', reason: 'period ends after window' },
          { period: 'Nov 15 – Dec 14',window: 'Dec 1 – Dec 31', rate: '$6 / K calls', reason: 'period ends within window', active: true },
        ],
      },
    },
  },

  {
    id: 'ecommerce',
    label: 'E-commerce',

    product: {
      companyName: 'ShopMart',
      products: ['Marketplace', 'Fulfillment', 'Payments'],
      primaryProduct: 'Marketplace',
      singleTagExample: {
        tagDim: 'Category',
        tagValues: ['Electronics', 'Fashion', 'Home & Living'],
        feeComponents: [
          { name: 'Transaction Fee',    unit: 'transaction', rates: ['$0.025', '$0.015', '$0.018'] },
          { name: 'Listing Fee',        unit: 'SKU/month',   rates: ['$0.10',  '$0.08',  '$0.09']  },
          { name: 'Payment Processing', unit: '% GMV',       rates: ['0.30%',  '0.20%',  '0.25%']  },
        ],
      },
      multiTagExample: {
        tags: [
          { label: 'Brand',    values: ['Brand-1', 'Brand-2']       },
          { label: 'Category', values: ['Electronics', 'Fashion']   },
        ],
      },
      tree: [
        { name: 'Marketplace', tags: ['Brand', 'Category'],       components: ['Transaction Fee', 'Listing Fee', 'Payment Processing'] },
        { name: 'Fulfillment', tags: ['Zone', 'Weight Class'],     components: ['Pick & Pack Fee', 'Storage Fee', 'Shipping Fee']      },
        { name: 'Payments',    tags: ['Brand'],                    components: ['Processing Fee', 'Refund Fee', 'Chargeback Fee']      },
      ],
      usageEvent: {
        product: 'Marketplace',
        tags: 'Brand = Brand-1, Category = Electronics',
        feeComponent: 'Transaction Fee',
        quantity: '5,000 transactions',
      },
    },

    feeComponent: {
      examples: ['Transaction Fee', 'Listing Fee', 'Payment Processing'],
      invoice: {
        id: 'INV-2024-1103',
        due: 'Due Mar 31, 2024',
        rows: [
          { accent: 'purple', name: 'Transaction Fee',    note: '5,000 transactions', qty: '5,000',  rate: '$0.020', amount: '$100.00' },
          { accent: 'teal',   name: 'Listing Fee',        note: '200 active SKUs',    qty: '200',    rate: '$0.10',  amount: '$20.00'  },
          { accent: 'amber',  name: 'Payment Processing', note: '$12,500 GMV',        qty: '12,500', rate: '0.25%',  amount: '$31.25'  },
        ],
        total: '$151.25',
      },
    },

    rateAttributes: {
      useCases: [
        ['Category-based commission',  'Category',          'Electronics, Fashion, Home'],
        ['Multi-brand marketplace',    'Brand',             'Brand-A, Brand-B'],
        ['Brand + category pricing',   'Brand, Category',   'Brand-A × Electronics …'],
        ['Seller tier commission',     'Seller Tier',       'Standard, Gold, Platinum'],
      ],
      singleDim: {
        intro: 'A marketplace with category-dependent commission rates defines a Category dimension on the product:',
        headers: ['Category'],
        rows: [['Electronics'], ['Fashion'], ['Home & Living']],
        outro: 'Fee components that use Category are priced once per category. Each carries its own commission rate in the pricing plan.',
        dimLabel: 'Category',
      },
      multiDim: {
        intro: 'A multi-brand marketplace with per-brand, per-category rates defines Brand and Category on the product:',
        headers: ['Brand', 'Category'],
        rows: [
          ['Brand-1', 'Electronics'],
          ['Brand-1', 'Fashion'],
          ['Brand-2', 'Electronics'],
          ['Brand-2', 'Fashion'],
        ],
        dims: [
          { key: 'brand',    label: 'Brand',    values: ['Brand-1', 'Brand-2']     },
          { key: 'category', label: 'Category', values: ['Electronics', 'Fashion'] },
        ],
      },
      partialTags: {
        productDims: [
          { key: 'brand',      label: 'Brand',       values: ['Brand-1', 'Brand-2'] },
          { key: 'category',   label: 'Category',    values: ['Electronics', 'Fashion'] },
          { key: 'sellerTier', label: 'Seller Tier', values: ['Standard', 'Gold'] },
        ],
        components: [
          { name: 'Transaction Fee',    uses: ['brand', 'category'] },
          { name: 'Listing Fee',        uses: ['category'] },
          { name: 'Payment Processing', uses: ['brand'] },
        ],
      },
      invoiceBehavior: 'A seller with sales across two categories sees two Transaction Fee lines — one per category — each at that category\'s commission rate.',
    },

    billableMetric: {
      product: 'Marketplace',
      currentFlow: {
        feeComponent: 'Transaction Fee',
        usageAttribute: 'GMV ($)',
        unit: 'dollars of GMV',
        groups: [
          { refId: 'TXN_1', calc: '120', total: 120 },
          { refId: 'TXN_2', calc: '45',  total: 45  },
          { refId: 'TXN_3', calc: '200', total: 200 },
          { refId: 'TXN_4', calc: '30',  total: 30  },
        ],
        finalSum: 395,
        rateCardLabel: '2% of GMV (flat)',
        rateCardCalc: '$395 × 2% = $7.90',
        invoiceLine: 'Transaction Fee',
        charge: '$7.90',
        transitionNote: 'Notice what happened at step 3 — all four transactions are summed regardless of category. This works if every order pays the same rate. But if Electronics orders need a different rate than Fashion or Home, there is no way to express that in this flow.',
      },
      problems: [
        { title: 'Charging a subset of events',          desc: 'The sum includes all categories. If the Electronics Transaction Fee applies only to Electronics orders, there is no way to exclude Fashion and Home orders from the sum.' },
        { title: 'Recorded unit ≠ rate card unit',        desc: 'If the rate card is expressed per $1,000 GMV but usage is recorded in raw dollars, the values going into the rate card are off by a factor of 1,000.' },
        { title: 'Different formula per event type',      desc: 'If forward orders and returns carry different fee structures, a single GMV sum cannot route each type to its own rate.' },
        { title: 'Threshold charging with accurate counts', desc: 'A zero-rate tier workaround produces the right total, but sellers see all 4 transactions on the invoice — not just the 3 that exceeded the $50 fulfilment threshold.' },
      ],
      filterExamples:     ['Category = Electronics', 'GMV > 50', 'Return Reason ≠ —'],
      expressionExamples: ['GMV / 1000', 'GMV', 'Units Sold'],
      eventCols: [
        { key: 'refId',        label: 'Transaction ID',  type: 'ref'     },
        { key: 'gmv',          label: 'GMV ($)',          type: 'usage'   },
        { key: 'units',        label: 'Units Sold',       type: 'usage'   },
        { key: 'category',     label: 'Category',         type: 'product' },
        { key: 'returnReason', label: 'Return Reason',    type: 'product' },
      ],
      events: [
        { refId: 'TXN_1', gmv: 120, units: 2, category: 'Electronics', returnReason: '—'          },
        { refId: 'TXN_2', gmv: 45,  units: 3, category: 'Fashion',     returnReason: 'wrong_item' },
        { refId: 'TXN_3', gmv: 200, units: 1, category: 'Electronics', returnReason: '—'          },
        { refId: 'TXN_4', gmv: 30,  units: 4, category: 'Home',        returnReason: '—'          },
      ],
      filterScenario: {
        fee: 'Electronics Transaction Fee',
        problem: 'The electronics-specific fee should only apply to Electronics orders. Without a filter, all $395 GMV is summed, inflating the electronics charge with Fashion and Home orders.',
        metricName: 'Electronics GMV',
        filter: 'Category = Electronics',
        expression: 'GMV',
        aggregation: 'Sum',
        before: '$395 GMV (all categories summed)',
        after:  '$320 GMV — TXN_1 ($120) + TXN_3 ($200) only',
      },
      transformScenario: {
        fee: 'Transaction Fee',
        problem: 'The rate card is expressed per $1,000 GMV. Without a transform, the billing engine receives raw dollar values and mismatches the rate card unit.',
        metricName: 'GMV in Thousands',
        filter: '(none)',
        expression: 'GMV / 1000',
        aggregation: 'Sum',
        before: '$395 piped to rate card — mismatches rate card unit ($1K)',
        after:  '0.395 units of $1K piped to rate card — correct unit',
      },
      conditionalScenario: {
        fee: 'Transaction Fee',
        problem: 'Forward orders and returns carry different fee structures. A single GMV expression cannot route each type to its own rate.',
        metrics: [
          { name: 'Forward GMV', filter: 'Return Reason = —',   expression: 'GMV', aggregation: 'Sum', result: '$120 + $200 + $30 = $350' },
          { name: 'Return GMV',  filter: 'Return Reason ≠ —',   expression: 'GMV', aggregation: 'Sum', result: '$45' },
        ],
      },
      thresholdScenario: {
        fee: 'Fulfilment Fee',
        problem: 'Fulfilment fee applies only to orders with GMV above $50. A rate card workaround ($0 tier below $50) produces the right total but shows 4 transactions instead of the expected 3.',
        metricName: 'Qualifying GMV',
        filter: 'GMV > 50',
        expression: 'GMV',
        aggregation: 'Sum',
        before: '4 transactions in report — includes TXN_4 ($30) at $0, misleading sellers',
        after:  '3 transactions: TXN_1, TXN_2, TXN_3 — count and value both correct',
      },
      metrics: [
        { name: 'Electronics GMV', fee: 'Electronics Transaction Fee', filter: 'Category = Electronics', expression: 'GMV',       aggregation: 'Sum' },
        { name: 'Forward GMV',     fee: 'Transaction Fee',             filter: 'Return Reason = —',      expression: 'GMV',       aggregation: 'Sum' },
        { name: 'Return GMV',      fee: 'Return Processing Fee',       filter: 'Return Reason ≠ —',      expression: 'GMV',       aggregation: 'Sum' },
      ],
    },

    plan: {
      currency: '$',
      plans: [
        {
          name: 'Basic Plan',
          rateCard: 49,
          duration: '1 month',
          fees: [
            {
              product: 'Marketplace',
              name: 'Transaction Fee',
              paymentMode: 'prepaid',
              startsAt: 'With contract',
              duration: '1 month',
              billingCycle: '1 month',
              grant: { type: 'Quantity-only', amount: 200, unit: 'transactions' },
              overageRate: '$0.025 / transaction', overageRateNum: 0.025,
            },
            {
              product: 'Marketplace',
              name: 'Listing Fee',
              paymentMode: 'prepaid',
              startsAt: 'With contract',
              duration: '1 month',
              billingCycle: '1 month',
              grant: { type: 'Quantity-only', amount: 50, unit: 'SKUs' },
              overageRate: '$0.10 / SKU', overageRateNum: 0.10,
            },
          ],
        },
        {
          name: 'Growth Plan',
          rateCard: 149,
          duration: '1 month',
          fees: [
            {
              product: 'Marketplace',
              name: 'Transaction Fee',
              paymentMode: 'prepaid',
              startsAt: 'With contract',
              duration: '1 month',
              billingCycle: '1 month',
              grant: { type: 'Quantity-only', amount: 1000, unit: 'transactions' },
              overageRate: '$0.020 / transaction', overageRateNum: 0.020,
            },
            {
              product: 'Marketplace',
              name: 'Listing Fee',
              paymentMode: 'prepaid',
              startsAt: 'With contract',
              duration: '1 month',
              billingCycle: '1 month',
              grant: { type: 'Quantity-only', amount: 250, unit: 'SKUs' },
              overageRate: '$0.08 / SKU', overageRateNum: 0.08,
            },
          ],
        },
      ],
      overageExample: {
        plan: 'Basic Plan',
        planPrice: 49,
        fee: 'Transaction Fee',
        included: 200,
        used: 285,
        overage: 85,
        overageRate: 0.025,
        overageCharge: 2.13,
        unit: 'transactions',
        otherFee: { name: 'Listing Fee', used: 38, included: 50, overageCharge: 0 },
      },
      contractExample: {
        customer: 'Acme Electronics',
        plans: [
          { name: 'Growth Plan', price: '$149' },
        ],
        overages: [
          { fee: 'Transaction Fee (overage)', note: '320 excess transactions × $0.020', charge: '$6.40' },
        ],
        adHocFees: [
          { name: 'Chargeback Fee', note: '3 chargebacks × $15', charge: '$45' },
        ],
        total: '$200.40',
      },
      carryForward: {
        fee: 'Transaction Fee',
        included: 200,
        unit: 'transactions',
        cycles: [
          { label: 'Month 1', used: 140, carried: 60,  balance: 260 },
          { label: 'Month 2', used: 200, carried: 60,  balance: 260 },
          { label: 'Month 3', used: 285, carried: 0,   balance: 200, overageNote: '25 at $0.025 = $0.63' },
        ],
      },
      carryForwardLimited: {
        cfUntilPeriod: 3,
        fees: [
          { name: 'Transaction Fee', included: 200, unit: 'transactions', overageRateNum: 0.025, overageRate: '$0.025 / transaction' },
          { name: 'Listing Fee',     included: 50,  unit: 'SKUs',         overageRateNum: 0.10,  overageRate: '$0.10 / SKU'          },
        ],
        // quiet months 1-2 build buffer, buffer lost at month-3 cutoff, month-4 spike hits overage
        defaultUsages: {
          'Transaction Fee': [150, 180, 210, 260, 190],
          'Listing Fee':     [40,  45,  48,  65,  48],
        },
      },
    },

    grant: {
      product: 'Marketplace',
      name: 'New Seller Waiver',
      description: 'Zero-rates all transaction fees for the first 30 days after contract activation.',
      type: 'duration',
      feeComponent: 'Transaction Fee',
      duration: { days: 30 },
      expiryDate: '2026-12-31',
      scopeTable: [
        { fee: 'Transaction Fee',       driver: 'GMV',        granted: true,  note: 'Grant applies — zero-rated for 30-day window' },
        { fee: 'Return Processing Fee', driver: 'GMV',        granted: false, note: 'Not in grant scope — billed at full rate'     },
        { fee: 'Fulfilment Fee',        driver: 'Units Sold', granted: false, note: 'Different meter'                   },
      ],
      billLines: [
        { name: 'New Seller Waiver', isGrant: true, qty: '1', rate: '₹0', amount: '₹0' },
      ],
    },

    productAttribute: {
      product: 'Marketplace',
      description: 'Extra context recorded with each transaction event — not used in pricing, but available for analytics and seller reporting.',
      attributes: [
        { name: 'Category',       type: 'Enum',   values: 'Electronics, Fashion, Home & Living', description: 'Product category of the item sold' },
        { name: 'Seller Tier',    type: 'Enum',   values: 'Bronze, Silver, Gold',                description: "Seller's current programme tier" },
        { name: 'Return Reason',  type: 'Enum',   values: 'damaged, wrong_item, changed_mind',   description: 'Reason code if the order was returned' },
      ],
      sampleTable: {
        usageAttrs: ['GMV ($)', 'Units Sold', 'Shipping Days'],
        productAttrs: ['Category'],
        rows: [
          { refId: 'TXN_1', usageVals: ['120', '2',  '3'], productVals: ['Electronics'] },
          { refId: 'TXN_2', usageVals: ['45',  '3',  '5'], productVals: ['Fashion']     },
          { refId: 'TXN_3', usageVals: ['200', '1',  '2'], productVals: ['Electronics'] },
          { refId: 'TXN_4', usageVals: ['30',  '4',  '4'], productVals: ['Home & Living'] },
        ],
      },
    },

    usageAttribute: {
      examples: [
        {
          feeComponent: 'Payment Processing',
          attribute: 'GMV',
          unit: 'currency value ($)',
          description: 'The system records the gross merchandise value of each transaction. That measurement drives the Payment Processing charge. The customer sees the fee — not the raw GMV figure.',
        },
        {
          feeComponent: 'Transaction Fee',
          attribute: 'Transaction Count',
          unit: 'transactions',
          description: 'The system counts each completed transaction. That drives the Transaction Fee charge. Switching to value-based pricing later means adding a new attribute and remapping — the invoice label stays unchanged.',
        },
      ],
      oneToMany: [
        { attribute: 'GMV',               feeComponents: ['Payment Processing', 'Commission Fee'] },
        { attribute: 'Transaction Count', feeComponents: ['Transaction Fee', 'Fraud Check Fee'] },
        { attribute: 'Active SKUs',       feeComponents: ['Listing Fee', 'Catalog Fee'] },
      ],
      manyToOne: {
        feeComponent: 'Seller Fee',
        attributes: ['Transaction Count', 'GMV'],
      },
      multipliers: [
        { attribute: 'GMV ($)',            multiplier: '0.0025', feeComponent: 'Payment Processing ($)', effect: '$1 GMV → $0.0025' },
        { attribute: 'Transaction Count', multiplier: '0.02',  feeComponent: 'Transaction Fee ($)',     effect: '1 tx → $0.02' },
        { attribute: 'Active SKUs',       multiplier: '0.10',  feeComponent: 'Listing Fee ($)',         effect: '1 SKU → $0.10/month' },
      ],
    },

    usageGrouping: {
      feeComponent: 'Transaction Fee',
      tiersNote: 'Tier 1: 1–100 transactions → $0.025 each   ·   Tier 2: 101+ transactions → $0.015 each',
      records: [
        { qty: '60 transactions' },
        { qty: '80 transactions' },
      ],
      separate: {
        refIds: ['batch-morning', 'batch-evening'],
        results: [
          { calc: '60 × $0.025  (Tier 1)', charge: '$1.50' },
          { calc: '80 × $0.025  (Tier 1)', charge: '$2.00' },
        ],
        total: '$3.50',
      },
      grouped: {
        refId: 'jan-14-txns',
        aggregatedQty: '140 transactions',
        tiers: [
          { calc: '100 × $0.025  (Tier 1)', charge: '$2.50' },
          { calc: '40 × $0.015   (Tier 2)', charge: '$0.60' },
        ],
        total: '$3.10',
      },
    },

    usage: {
      product: 'Marketplace',
      customer: 'Fashion House Pvt. Ltd.',
      dedup: {
        batchId: 'batch-jan-14-txns',
        attr: 'Transaction Count',
        first:  { value: '50 transactions', note: 'sent at 2:05 PM — missed a few late-settling orders' },
        second: { value: '55 transactions', note: 'sent at 2:22 PM — corrected after order reconciliation' },
      },
      usageAttr: { label: 'Transaction Count', value: '50', unit: 'transactions' },
      rateAttrs: [{ label: 'Brand', value: 'Brand-1' }, { label: 'Category', value: 'Electronics' }],
      feeComponent: 'Transaction Fee',
      timestamp: '14 Jan 2025, 02:05 PM',
      billingCycle: { start: '1 Jan 2025', end: '31 Jan 2025' },
      cycleNote: 'This usage falls inside the January billing cycle and will be accumulated toward the January invoice for Fashion House.',
      partialIngestion: {
        rateAttr: { label: 'Category', value: 'Electronics' },
        contracts: [
          { label: 'Contract A', feeComponent: 'Transaction Fee', usageAttr: 'Transaction Count' },
          { label: 'Contract B', feeComponent: 'Listing Fee',     usageAttr: 'Active SKUs' },
        ],
        measurements: [
          { attr: 'Transaction Count', value: '50 transactions', matched: true,  reason: 'Rate card for Category = Electronics found in Contract A' },
          { attr: 'Active SKUs',       value: '10 SKUs',         matched: false, reason: 'No rate card for Category = Electronics in Contract B' },
        ],
      },
    },

    proration: {
      fixedFee: 50,
      compositeFixed: 20,
      compositeRate: 2,
      compositeUnit: 'transaction',
      compositeUnitPlural: 'transactions',
    },

    billingReport: {
      report: {
        reportId: 'BR-2025-0183',
        customer: 'Acme Marketplace LLC',
        period: { start: '1 Jan 2025', end: '31 Jan 2025' },
        rows: [
          { name: 'Transaction Fee',     note: '5,200 transactions × $0.020', qty: '5,200', rate: '$0.020', amount: '$104.00' },
          { name: 'Payment Processing',  note: '5,200 transactions × $0.010', qty: '5,200', rate: '$0.010', amount: '$52.00'  },
        ],
        total: '$156.00',
      },
      convergence: {
        fees: [
          { name: 'Transaction Fee',    cycle: 'monthly'   },
          { name: 'Payment Processing', cycle: 'monthly'   },
          { name: 'Listing Fee',        cycle: 'quarterly' },
        ],
        dates: [
          { label: 'Jan 31', fees: ['Transaction Fee', 'Payment Processing'] },
          { label: 'Feb 28', fees: ['Transaction Fee', 'Payment Processing'] },
          { label: 'Mar 31', fees: ['Transaction Fee', 'Payment Processing', 'Listing Fee'] },
        ],
      },
      convergenceNote: 'Transaction Fee and Payment Processing are billed monthly; Listing Fee quarterly. In January and February only the monthly fees are included. In March all three cycle ends align — one report covers all three.',
    },

    rateCardWildcard: {
      exampleBefore: 'brand: "Brand-1"',
      migration: {
        beforeDims: ['Brand'],
        newDim: 'Category',
        cards: [
          { id: 'rc-1', before: ['Brand-1'], rate: '$0.025 / tx' },
          { id: 'rc-2', before: ['Brand-2'], rate: '$0.020 / tx' },
        ],
      },
      lookup: {
        cards: [
          {
            id: 'rc-1',
            dims: [{ label: 'Brand', value: 'Brand-1' }, { label: 'Category', isAny: true, value: null }],
            rate: '$0.025/tx',
            covers: 'Brand-1 × any category (fallback)',
          },
          {
            id: 'rc-2',
            dims: [{ label: 'Brand', value: 'Brand-1' }, { label: 'Category', value: 'Electronics' }],
            rate: '$0.030/tx',
            covers: 'Brand-1 × Electronics only (overrides above)',
          },
          {
            id: 'rc-3',
            dims: [{ label: 'Brand', value: 'Brand-2' }, { label: 'Category', isAny: true, value: null }],
            rate: '$0.020/tx',
            covers: 'Brand-2 × any category',
          },
        ],
        queries: [
          { dims: [{ label: 'Brand', value: 'Brand-1' }, { label: 'Category', value: 'Electronics' }], rate: '$0.030/tx', matchType: 'specific', reason: 'specific wins' },
          { dims: [{ label: 'Brand', value: 'Brand-1' }, { label: 'Category', value: 'Fashion' }],     rate: '$0.025/tx', matchType: 'wildcard', reason: 'wildcard fallback' },
          { dims: [{ label: 'Brand', value: 'Brand-2' }, { label: 'Category', value: 'Electronics' }], rate: '$0.020/tx', matchType: 'wildcard', reason: 'wildcard fallback' },
        ],
      },
      grid: {
        dims: ['Brand', 'Category'],
        rows: [
          { cells: [{ value: 'Brand-1' }, { isAny: true }], rate: '$0.025 / tx', isWildcard: true },
          { cells: [{ value: 'Brand-1' }, { value: 'Electronics' }], rate: '$0.030 / tx' },
          { cells: [{ value: 'Brand-2' }, { isAny: true }], rate: '$0.020 / tx', isWildcard: true },
        ],
      },
    },

    rateCardMatrix: {
      feeComponent: 'Transaction Fee',
      tagLabels: ['Brand', 'Category'],
      combinations: [
        { tags: ['Brand-1', 'Electronics'], type: 'Per Unit', value: '$0.025 / tx' },
        { tags: ['Brand-1', 'Fashion'],     type: 'Fixed',    value: '$50.00 / month' },
        { tags: ['Brand-2', 'Electronics'], type: 'Per Unit', value: '$0.020 / tx' },
        { tags: ['Brand-2', 'Fashion'],     type: 'Per Unit', value: '$0.015 / tx'  },
      ],
    },

    multiCurrency: {
      feeComponent: 'Transaction Fee',
      tagLabels: ['Brand', 'Category'],
      tags: ['Brand-1', 'Electronics'],
      rates: [
        { currency: 'USD', type: 'Per Unit', value: '$0.025 / tx', note: 'Customers billed in USD' },
        { currency: 'GBP', type: 'Per Unit', value: '£0.020 / tx', note: 'Customers billed in GBP' },
        { currency: 'AUD', type: 'Per Unit', value: 'A$0.038 / tx', note: 'Customers billed in AUD' },
      ],
    },

    contractFlow: {
      customers: [
        { id: 'cust-1', name: 'Fashion House Pvt. Ltd.',  taxId: 'GSTIN 07AABCF5678B1Z2', currency: 'INR' },
        { id: 'cust-2', name: 'Nykaa Fashion Ltd.',       taxId: 'GSTIN 27AABCN1234D1Z5', currency: 'INR' },
        { id: 'cust-3', name: 'Myntra Designs Pvt. Ltd.', taxId: 'GSTIN 29AABCM5678E1Z4', currency: 'INR' },
      ],
      availableTaxes: ['CGST 9%', 'SGST 9%', 'IGST 18%'],
      story: {
        scenario: "{customer} is moving their entire catalog to ShopMart's marketplace after three months of manual invoicing. Their head of partnerships just signed a 12-month agreement starting March 1st. This is ShopMart's largest fashion brand onboarding of the year — and the contract needs to be live before their catalog migration begins.",
        steps: {
          customer: {
            narrative: "Select the brand this contract is for. Each is a distinct legal entity with their own GST registration, billing address, and finance team.",
            prompt: "The walkthrough defaults to Fashion House. Switch to Nykaa or Myntra — two other fashion brands on the platform. Same marketplace services, same billing structure, but each would have negotiated different rates and discount schedules.",
          },
          components: {
            narrative: "The signed agreement covers all three marketplace services. {customer} specifically requested ShopMart's Payment Processing — they wanted to consolidate payment reconciliation rather than run a separate gateway. That's what pushed this deal over the line.",
            prompt: "Try removing Payment Processing. Some brands bring their own payment gateway, which means that line never appears on their invoice. {customer} made the opposite choice — but you can see what the contract looks like without it.",
          },
          configure: {
            narrative: "Commercial terms reflect {customer} as a high-potential new brand: steep early discounts to incentivize catalog migration, and a Listing Fee that doesn't kick in until June — after their full catalog is loaded.",
            fcNotes: {
              'Transaction Fee': "ShopMart's main revenue line. 20% discount for the first 3 months was the onboarding incentive that got {customer} to commit. It steps down to 10% from month 4 — still competitive, but reflecting their now-established presence.",
              'Listing Fee': "Prepaid and monthly, but starting June rather than March. {customer}'s catalog migration takes 3 months — they won't have products live for listing billing until then. The schedule end date (Feb 2025) aligns with the contract term.",
              'Payment Processing': "Monthly and postpaid, active for cycles 1–9. The 9-cycle cap is deliberate — if the contract renews, Payment Processing gets renegotiated separately based on GMV. The ₹1,000 fixed discount per period was a goodwill concession from ShopMart.",
            },
            modeSwitch: {
              toPrepaid: "You've switched to prepaid. For Transaction Fee, that would mean {customer} estimates their monthly GMV upfront — unusual for marketplace fees, which depend entirely on shopper behaviour. They'd likely push back on this.",
              toPostpaid: "Postpaid is the standard for transaction fees. {customer} pays based on what actually sold each month — no need to predict shopper behaviour in advance.",
            },
          },
          settings: {
            narrative: "{customer} committed to a minimum annual spend — ShopMart needed that to justify the onboarding investment. There's also a monthly cap to limit {customer}'s exposure if their GMV unexpectedly spikes. Auto-renew is on — both sides want continuity.",
            prompt: "Flip auto-renew off. Without it, {customer}'s listings would effectively pause at contract end unless someone manually renews. For a marketplace brand mid-season, that's a serious operational risk.",
          },
          summary: {
            closing: "Live from March 1st. The billing engine starts tracking {customer}'s transactions and listings. Monthly invoices go out for all three components — Transaction Fee with the 20% early discount, Listing Fee from June, Payment Processing with the ₹1,000 concession. The 8% pre-tax contract discount applies on top of everything.",
          },
        },
      },
    },

    contract: {
      customer: 'Fashion House Pvt. Ltd.',
      product: 'Marketplace',
      currency: '₹',
      autoRenew: true,
      contractDiscount: { type: 'percentage', value: 8, timing: 'pre-tax' },
      contractMinMax: { min: 50000, max: 5000000 },
      billingPeriodMinMax: {
        min: 5000,
        max: 200000,
        includedComponents: ['Transaction Fee', 'Payment Processing'],
      },
      contractStart: '1 Mar 2024',
      feeComponents: [
        {
          name: 'Transaction Fee',
          paymentMode: 'postpaid',
          billingCycle: 'Monthly',
          schedule: null,
          taxes: ['CGST 9%', 'SGST 9%'],
          discounts: [
            { fromPeriod: 1, toPeriod: 3,    type: 'percentage', value: 20 },
            { fromPeriod: 4, toPeriod: null, type: 'percentage', value: 10 },
          ],
        },
        {
          name: 'Listing Fee',
          paymentMode: 'prepaid',
          billingCycle: 'Monthly',
          schedule: { mode: 'date', start: '1 Jun 2024', end: '28 Feb 2025' },
          taxes: ['CGST 9%', 'SGST 9%'],
          estimates: [
            { usageAttribute: 'Listings', tags: 'Brand-1 / Electronics', value: '2,000 listings' },
            { usageAttribute: 'Listings', tags: 'Brand-1 / Apparel',     value: '5,000 listings' },
          ],
          discounts: [],
        },
        {
          name: 'Payment Processing',
          paymentMode: 'postpaid',
          billingCycle: 'Monthly',
          schedule: { mode: 'cycle', fromCycle: 1, toCycle: 9, resolvedStart: '1 Mar 2024', resolvedEnd: '30 Nov 2024' },
          taxes: [],
          discounts: [
            { fromPeriod: 1, toPeriod: null, type: 'fixed', value: 1000 },
          ],
        },
      ],
    },

    customer: {
      name: 'Fashion House Pvt. Ltd.',
      taxIdLabel: 'GSTIN',
      taxId: '07AABCF5678B1Z2',
      currency: 'INR',
      currencySymbol: '₹',
      billingAddress: {
        line1: '23, Connaught Place',
        city: 'New Delhi',
        state: 'Delhi',
        country: 'India',
      },
      shippingAddress: {
        line1: 'Plot 45, Sector 63',
        city: 'Noida',
        state: 'Uttar Pradesh',
        country: 'India',
      },
      taxNote: 'Interstate supply — billing in Delhi, shipping in Uttar Pradesh. IGST applies.',
      rateCardCurrencies: [
        { currency: 'INR', note: 'Customers billed in INR', active: true },
        { currency: 'USD', note: 'Customers billed in USD', active: false },
        { currency: 'AUD', note: 'Customers billed in AUD', active: false },
      ],
    },

    rateCard: {
      currency: '$',
      feeComponent: 'Transaction Fee',
      unit: 'transactions',
      fixed: { price: 50 },
      ratePerUnit: {
        rate: 2,
        examples: [
          { usage: 42, price: 84  },
          { usage: 89, price: 178 },
        ],
      },
      tiers: [
        { start: 0,   limit: 50,   rate: 2   },
        { start: 50,  limit: 100,  rate: 1.5 },
        { start: 100, limit: null, rate: 1   },
      ],
      graduated: {
        examples: [
          { usage: 40,  price: 80,  breakdown: '2 × 40' },
          { usage: 60,  price: 115, breakdown: '2 × 50 + 1.5 × 10' },
          { usage: 120, price: 195, breakdown: '2 × 50 + 1.5 × 50 + 1 × 20' },
        ],
      },
      volume: {
        examples: [
          { usage: 40,  price: 80,  breakdown: '2 × 40    (0–50 tier)'   },
          { usage: 60,  price: 90,  breakdown: '1.5 × 60  (50–100 tier)' },
          { usage: 120, price: 120, breakdown: '1 × 120   (100+ tier)'   },
        ],
      },
      minMax: {
        defaultFlatFee: 80,
        defaultRate: 2,
        defaultVariant: 'max',
        examples: [
          { usage: 30,  flatFee: 80,  calculated: 60,  calcNote: '30 × 2',   logic: 'Max', price: 80  },
          { usage: 60,  flatFee: 80,  calculated: 90,  calcNote: '60 × 1.5', logic: 'Max', price: 90  },
          { usage: 100, flatFee: 120, calculated: 100, calcNote: '100 × 1',  logic: 'Min', price: 100 },
        ],
      },
      timeBased: {
        feeComponent: 'Transaction Fee',
        unit: 'transaction',
        slot: 'Transaction Fee · Electronics · USD',
        standardRateLabel: '$2 / transaction',
        override: {
          rateLabel: '$1.50 / transaction',
          window: 'Nov 15 – Dec 31, 2026',
          name: 'Holiday Season Discount',
        },
        variant: {
          name: 'RC_002 (Enterprise)',
          rateLabel: '$1.20 / transaction',
        },
        periodTable: [
          { period: 'Oct 1 – Oct 31',  window: 'Nov 15 – Dec 31', rate: '$2 / transaction',    reason: 'period ends before window' },
          { period: 'Nov 15 – Nov 30', window: 'Nov 15 – Dec 31', rate: '$1.50 / transaction', reason: 'period ends within window', active: true },
          { period: 'Dec 1 – Dec 31',  window: 'Nov 15 – Dec 31', rate: '$1.50 / transaction', reason: 'period ends within window', active: true },
          { period: 'Jan 1 – Jan 31',  window: 'Nov 15 – Dec 31', rate: '$2 / transaction',    reason: 'period ends after window' },
        ],
      },
    },
  },
];
